import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  changeSessionServer,
  endSession,
  fetchSessionKit,
  markBriefingRead,
  rotateSessionCode,
  startSession,
  type Objective,
  type RoleKit,
} from '../api/missions';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { QrCode } from './QrCode';
import { DeltaList } from './CampaignBoard';
import { CommanderBand } from './CommanderBand';
import { JoinCard, ObjectivesCard, VotesPanel } from './DecisionPanels';
import { NextChapterCard } from './NextChapterCard';
import { OutcomeMiniMap } from './OutcomeMiniMap';
import { fetchCampaign, type ChapterDelta, type StandingView } from '../api/campaigns';
import { fetchSessionAftermath, type MatchDetail } from '../api/reports';
import { fetchPlayableServers, type PlayableServer } from '../api/servers';
import { deleteSession, fetchSessionAttendees, type Attendee } from '../api/sessions';
import { OBJECTIVE_ICONS } from './objectiveIcons';
import { SupportTiles } from './SupportPicture';
import { supportKeysOf } from './supportIcons';
import { SessionWhen } from './SessionWhen';
import { ServerOptions } from './ServerOptions';
import { usable } from './serverFit';
import { useRatedServers } from './useRatedServers';
import { worldSizeOf } from './designModel';
import { MatchTables } from './MatchArmament';
import { AssetPicture } from './AssetPicture';
import { derivePhases } from './phases';
import { MissionMap } from './MissionMap';
import { MissionSides } from './MissionSides';
import { useObjectiveEditor } from './objectiveEditing';
import { useConfirm } from './useConfirm';
import { Planned } from './Planned';
import { Shell, type BarItem } from './Shell';
import { SessionTestsCard } from './SessionTestsCard';
import { ScriptPhases } from './ScriptPhases';
import { HowItWorksCard } from './HowItWorksCard';
import {
  BriefingStrip,
  ConnectionBanner,
  Figure,
  NowCard,
  RosterCard,
  SessionBanner,
  SessionHeader,
  SessionSkeleton,
  SessionTimeline,
  SupportsCard,
  WhoWasThere,
} from './SessionCards';
import { SessionMapLayer } from './SessionMapLayer';
import {
  briefingSupportText,
  casualtyCounts,
  clockOfDay,
  connectionOf,
  currentObjectiveOf,
  currentPhase,
  distanceTo,
  gridRef,
  holdClockOf,
  inGameMinutes,
  isHiddenObjective,
  lastPlatformAway,
  lastSnapshot,
  latestCommandDecision,
  latestRadio,
  playerObjectives,
  playersOnServer,
  progressOf,
  supportsState,
  timelineOf,
} from './sessionState';
import type { PlatformAway } from './sessionState';
import { errorText, objectiveStatesOf, useSessionLive, withAdded } from './useSessionLive';

const fmtWhen = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ca', { dateStyle: 'medium', timeStyle: 'short' }) : null;

/** Seconds between an ISO instant and now, or null when there is no instant. */
const secondsFrom = (iso: string | null | undefined, now: number) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.floor((now - t) / 1000) : null;
};

/**
 * The band for what a live night's strip cannot say in a pill: the alarms of a
 * night that is live in the record with nothing behind it (no server, a
 * stall). The countdown of a session that waits and the outcome of one that is
 * over are the banner's own large line.
 */
function StateBand({
  state,
  stalled = null,
  noServer = false,
}: {
  state: string;
  /** Live with no server assigned at all: nothing is starting, whatever the record says. */
  noServer?: boolean;
  /**
   * Live in the record with no server behind it: 'restartLoop' when the
   * platform stopped a server that could not get the mission past its init,
   * 'serverDown' when the panel reports the server is not running.
   */
  stalled?: string | null;
}) {
  if (state === 'LIVE' && noServer) {
    return (
      <section className="band scheduled" aria-label={ca.live.noServerTitle} role="alert">
        <i className="bi bi-exclamation-triangle" aria-hidden="true" />
        <strong className="band-title">{ca.live.noServerTitle}</strong>
        <span className="grow" />
        <span className="muted small">{ca.live.noServerHint}</span>
      </section>
    );
  }
  if (state === 'LIVE' && stalled) {
    const down = stalled === 'serverDown';
    return (
      <section
        className="band scheduled"
        aria-label={down ? ca.live.downTitle : ca.live.loopTitle}
        role="alert"
      >
        <i className="bi bi-exclamation-triangle" aria-hidden="true" />
        <strong className="band-title">{down ? ca.live.downTitle : ca.live.loopTitle}</strong>
        <span className="grow" />
        <span className="muted small">{down ? ca.live.downHint : ca.live.loopHint}</span>
      </section>
    );
  }
  return null;
}

/**
 * A start that is under way (2026-10-26): the platform stops the server on purpose, sets the
 * mission and starts it again, and for those minutes the page said «El servidor no està en
 * marxa» over a server it was starting. The step it is on, in a person's words.
 */
export function StartingBand({ step }: { step: string }) {
  return (
    <section className="band scheduled" aria-label={ca.live.startingTitle} role="status">
      <i className="bi bi-hourglass-split" aria-hidden="true" />
      <strong className="band-title">{ca.live.startingTitle}</strong>
      <span className="grow" />
      <span className="muted small">
        {ca.live.startingSteps[step] ?? ca.live.startingSteps.preparing}
      </span>
    </section>
  );
}

/** How long after the platform is back the page still says it was away. */
const AWAY_SHOWN_MS = 30 * 60_000;

/**
 * That the platform was away while the night went on, for how long and when,
 * once it is back (outage test a, 2026-10-18): the page had said only
 * "actualitzat ara mateix". Shown for half an hour after it came back.
 */
function AwayBand({ away, now }: { away: PlatformAway | null; now: number }) {
  if (!away || now - away.to > AWAY_SHOWN_MS) return null;
  const hour = (ms: number) =>
    new Date(ms).toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' });
  const minutes = Math.max(1, Math.round((away.to - away.from) / 60_000));
  return (
    <section className="band away" aria-label={ca.live.awayTitle(minutes)}>
      <i className="bi bi-cloud-slash" aria-hidden="true" />
      <strong className="band-title">{ca.live.awayTitle(minutes)}</strong>
      <span className="grow" />
      <span className="muted small">{ca.live.awayHint(hour(away.from), hour(away.to))}</span>
    </section>
  );
}

/**
 * The night's mods as a file the Arma 3 Launcher imports (the owner,
 * 2026-09-19): drag it on the Launcher and the mods and Creator DLCs select
 * themselves, the missing ones queued for download. It is the list the mission
 * was made with, from its own definition, and whoever can see the session can
 * take it. Nothing is drawn for a mission that lists no mods. `collapsed` folds
 * it under its own title, for the foot of the briefing.
 */
export function LauncherMods({
  code,
  mods,
  dlcs,
  collapsed = false,
}: {
  code: string;
  mods: number;
  dlcs: number;
  collapsed?: boolean;
}) {
  if (mods === 0) return null;
  const body = (
    <>
      <p className="muted small">{ca.sessionMods.count(mods, dlcs)}</p>
      <a className="button" href={`/api/sessions/${code}/mods.html`} download>
        <i className="bi bi-download" aria-hidden="true" /> {ca.sessionMods.download}
      </a>
      <p className="muted small">{ca.sessionMods.hint}</p>
    </>
  );
  return (
    <article className="card" aria-label={ca.sessionMods.title}>
      {collapsed ? (
        <details className="how-it-works">
          <summary>
            <h2>{ca.sessionMods.title}</h2>
          </summary>
          {body}
        </details>
      ) : (
        <>
          <h2>{ca.sessionMods.title}</h2>
          {body}
        </>
      )}
    </article>
  );
}

/**
 * The most people the server ever reported at once, by its health reports; null
 * when it never said. People only: the engine counts a headless client in
 * `players` once it has joined, and the report that carries `headless` beside
 * it says how many to take off (a player's copy has them taken off already).
 */
function peakPlayers(events: { kind: string; payload: { server?: Record<string, unknown> } }[]) {
  const counts = events
    .filter((e) => e.kind === 'health')
    .map((e) => {
      const headless = Number(e.payload.server?.headless);
      return Number(e.payload.server?.players) - (Number.isFinite(headless) ? headless : 0);
    })
    .filter((n) => Number.isFinite(n))
    .map((n) => Math.max(0, n));
  return counts.length ? Math.max(...counts) : null;
}

/**
 * When an objective of the briefing starts, in words: from the start, once the objectives the
 * players are shown before it are done, or, when what it waits for is still hidden from them,
 * once another objective is done (never naming it). A hidden objective the runtime has given is
 * one the night handed out while it was played.
 */
function objectiveStart(o: Objective, titleById: Map<string, string>): string {
  const before = o.dependsOn ?? [];
  const known = before.filter((id) => titleById.has(id)).map((id) => titleById.get(id) ?? '');
  if (known.length > 0) return ca.sessionPage.objectiveAfter(known);
  if (before.length > 0) return ca.sessionPage.objectiveAfterUnknown;
  if (isHiddenObjective(o)) return ca.sessionPage.objectiveGiven;
  return ca.sessionPage.objectiveFromStart;
}

/**
 * The session page (`/s/:code`) and its briefing (`/s/:code/briefing`).
 *
 * The page has three faces, by the session's own state (the owner,
 * 2026-10-12): before the night it is an invitation (the banner with the
 * countdown, who fights whom, how to get in, whatever is left to a vote);
 * while the night runs it is the phone beside the game (NOW, the map, the
 * objectives a player may know, the radio); once it is over it is the result
 * (the outcome, the figures, who was there, what happened in order).
 *
 * One rule outranks the layout: a player gets no spoilers. Nothing here tells
 * what will happen later: no hidden objective before the runtime gives it, no
 * move of the enemy's, no twist, nothing of what a piece of intel said while
 * the night runs. The platform keeps all of that out of a player's copy
 * (`MissionService.maskHidden`, `SessionEventMask`) and hands this page that copy
 * whoever reads it (the mission's author and an admin included: the director's
 * copy is for the direction page, which asks for it); the page also draws only
 * what a player is shown, so a copy that arrived with more would still show no
 * more. Whoever directs is given the way to the direction page instead.
 */
export function SessionPage({ briefing = false }: { briefing?: boolean }) {
  const { code = '' } = useParams();
  const { me } = useAuth();
  const navigate = useNavigate();
  const live = useSessionLive(code);
  const { data, events, votes, error, setData, setError, setVotes } = live;
  const [qr, setQr] = useState(false);
  const [handedTo, setHandedTo] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const [standings, setStandings] = useState<StandingView[]>([]);
  const [kit, setKit] = useState<RoleKit[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [selected, setSelected] = useState<string | null>(null);
  /** The map tab (UI doc 5.1): the map as a full-screen layer over the page. */
  const [mapOpen, setMapOpen] = useState(false);
  /**
   * What the night left behind, out of the panel's own count: the tasks and how
   * each ended, the kill feed, the weapons and vehicles, and where people died.
   * Asked for once the night is over and never before, and absent whenever the
   * panel has no report for it — which is the ordinary case for a session that
   * was never played on a server it watches.
   */
  const [aftermath, setAftermath] = useState<MatchDetail | null>(null);
  /** Who was there, with their pictures, once the night is over; members only, and empty when the platform has no list. */
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  /**
   * The servers a director may start, offered when the session was made
   * without one: the start used to go through with nothing behind it and the
   * band said the server was starting (2026-09-12).
   */
  const [serversToPick, setServersToPick] = useState<PlayableServer[]>([]);
  const [serverForStart, setServerForStart] = useState<number | null>(null);
  // This tab pressed the start and its request has not come back: the platform's own word for
  // where the start is (`data.starting`) arrives by asking, so the page asks every few seconds.
  const [startBusy, setStartBusy] = useState(false);
  const startUnderWay = startBusy || !!data?.starting;
  const refreshPage = live.refresh;
  useEffect(() => {
    if (!startUnderWay) return undefined;
    const timer = setInterval(() => void refreshPage(), 3000);
    return () => clearInterval(timer);
  }, [startUnderWay, refreshPage]);
  // Until the session starts its server can be changed among the ones that can
  // run the mission, rated the way the mission page rates them (2026-09-18).
  const canPickServer = !!data && data.canDirect && data.session.state === 'SCHEDULED';
  const { rated: ratedServers, busyServers } = useRatedServers(
    data?.missionCode ?? null,
    canPickServer,
  );
  const needsServer =
    !!data && data.canDirect && data.session.panelServerId === null && !canPickServer;
  useEffect(() => {
    if (!needsServer) return;
    let current = true;
    fetchPlayableServers()
      .then((r) => {
        if (current) setServersToPick(r.servers.filter(usable));
      })
      .catch(() => {
        if (current) setServersToPick([]);
      });
    return () => {
      current = false;
    };
  }, [needsServer]);
  const campaignCode = data?.campaign?.code ?? null;
  const editor = useObjectiveEditor(
    data?.missionCode ?? '',
    !!data && me.authenticated && !briefing,
    setError,
    live.refresh,
  );

  // The heartbeat ticks once a second so "fa N s" and the countdown stay honest.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // The briefing of a campaign chapter reads out the ranks (plan 5.4).
  useEffect(() => {
    if (!briefing || !campaignCode) return;
    fetchCampaign(campaignCode)
      .then((c) => setStandings(c.standings))
      .catch(() => undefined);
  }, [briefing, campaignCode]);

  // What each role carries (UI doc 5.2): the loadout the publisher writes into
  // the slot, resolved the same way, so the briefing never promises a rifle the
  // server will not hand out. The platform answers it to an account only.
  useEffect(() => {
    if (!briefing || !code || !me.authenticated) return;
    fetchSessionKit(code)
      .then(setKit)
      .catch(() => setKit([]));
  }, [briefing, code, me.authenticated]);

  // What the night left behind, once it is over. Asked for once; a report that
  // has ended does not change, and a session the panel has no report for simply
  // never fills this in.
  useEffect(() => {
    if (!code || data?.session.state !== 'ENDED' || !me.authenticated) return;
    let live = true;
    fetchSessionAftermath(code)
      .then((d) => live && setAftermath(d))
      .catch(() => live && setAftermath(null));
    return () => {
      live = false;
    };
  }, [code, data?.session.state, me.authenticated]);

  // Who was there, with their pictures: the platform's list of the people of the
  // night (never a headless client), asked for once it is over. A failure leaves
  // the snapshot's names, which the card falls back to.
  useEffect(() => {
    if (!code || data?.session.state !== 'ENDED' || !me.authenticated) return;
    let live = true;
    fetchSessionAttendees(code)
      .then((a) => live && setAttendees(a))
      .catch(() => live && setAttendees([]));
    return () => {
      live = false;
    };
  }, [code, data?.session.state, me.authenticated]);

  if (!data && live.unavailable) {
    // A night that is not there for this reader, whether it never was or it is
    // a private mission's: the platform answers both with a 404 and the page
    // says the same for both, with nothing to retry.
    return (
      <Shell title={ca.session} kicker={code} signInLabel={ca.sessionPage.signInAccount}>
        <div className="empty" role="alert">
          <i className="bi bi-lock" aria-hidden="true" />
          <p>{ca.sessionPage.unavailable}</p>
          <p className="muted">{ca.sessionPage.unavailableHint}</p>
          <Link className="button" to="/sessions">
            <i className="bi bi-calendar3" aria-hidden="true" /> {ca.sessionPage.unavailableBack}
          </Link>
        </div>
      </Shell>
    );
  }
  if (!data) {
    // The first paint is the cards' shapes (UI doc 7), and a failure says what
    // it was with a way to try again, never an empty page.
    return (
      <Shell title={ca.session} kicker={code} signInLabel={ca.sessionPage.signInAccount}>
        {error ? (
          <div className="cards">
            <p className="error" role="alert">
              {error}
            </p>
            <button type="button" onClick={() => void live.refresh()}>
              <i className="bi bi-arrow-clockwise" aria-hidden="true" /> {ca.sessionNow.retry}
            </button>
          </div>
        ) : (
          <SessionSkeleton />
        )}
      </Shell>
    );
  }
  const def = withAdded(data.definition, events);
  const objectiveStates = objectiveStatesOf(events);
  const shownTitle = (o: {
    id: string;
    title?: string;
    priority?: 'main' | 'secondary' | 'hidden';
    visibility?: 'hidden' | 'revealed';
  }) => {
    const seen = objectiveStates.get(o.id);
    if (isHiddenObjective(o))
      return seen && seen.title && seen.title !== '?' ? seen.title : ca.hiddenObjective;
    return o.title ?? o.id;
  };
  // The session is still "live" in the record and there is no server behind it:
  // the platform stopped it after the -autoInit restart loop, or the panel
  // reports it down. Either way there is nothing to join until a director
  // starts it again, and anything the runtime said afterwards clears it.
  const STALLED = ['restartLoop', 'serverDown'];
  // A word from before the last start is about the server that start replaced: a server the
  // platform stopped to start again was reported down, and went on being after it was up.
  const startedAtMs = data.session.startedAt ? Date.parse(data.session.startedAt) : NaN;
  const lastStall = [...events]
    .reverse()
    .find(
      (e) =>
        STALLED.includes(e.kind) &&
        !(Number.isFinite(startedAtMs) && Date.parse(e.receivedAt) < startedAtMs),
    );
  const stalled =
    data.session.state === 'LIVE' &&
    !!lastStall &&
    !events.some((e) => e.seq > lastStall.seq && !STALLED.includes(e.kind));
  const stallReason = stalled ? lastStall.kind : null;
  const away = lastPlatformAway(events);
  // Live with no server at all: a start that went through before the director had picked one.
  const noServer = data.session.panelServerId === null;
  // A start of the night is under way: said in place of "down" or "no server", and not offered again.
  const starting =
    data.starting ?? (startBusy ? { step: 'preparing', since: new Date().toISOString() } : null);
  const currentObjective = currentObjectiveOf(def.objectives, objectiveStates);
  const heartbeat =
    live.lastReceivedAt != null
      ? ca.live.updated(Math.max(0, Math.floor((now - live.lastReceivedAt) / 1000)))
      : null;

  const state = data.session.state;
  const member = me.authenticated;
  /** Whoever tests or administers: the only readers a class of the game is ever pointed out to. */
  const staff = me.roles.includes('ADMIN') || me.roles.includes('TESTER');
  const elapsed = secondsFrom(data.session.startedAt, now);
  // What a player is shown, and nothing else: on this page a director reads the
  // night the way the players do (the direction page is where the rest is).
  const shownObjectives = playerObjectives(def.objectives, objectiveStates);
  const connection = connectionOf({
    loaded: true,
    online: live.online,
    failing: live.failing,
    live: state === 'LIVE',
    lastReceivedAt: live.lastReceivedAt,
    now,
  });
  const snapshot = lastSnapshot(events);
  const supports = supportsState(def.supports, events, now);
  const onServer = playersOnServer(events);
  const inGame = state === 'LIVE' ? inGameMinutes(def, events, now) : null;
  const runtimeUp = (data.lastEventSeq ?? -1) >= 0 || events.length > 0;
  const openOnMap = (id: string | null) => {
    setSelected(id);
    setMapOpen(true);
  };
  const scrollToCard = (target: string) => {
    setMapOpen(false);
    document.querySelector(target)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  // The session's own bottom bar on a phone (UI doc 4): Now, Map and Roster,
  // then the account's own item and "More" from the shell.
  const bar: BarItem[] = [
    {
      key: 'now',
      label: ca.sessionNow.barNow,
      icon: 'lightning-charge',
      onClick: () => scrollToCard('main.page'),
      active: !mapOpen,
    },
    {
      key: 'map',
      label: ca.sessionNow.barMap,
      icon: 'map',
      onClick: () => setMapOpen(true),
      active: mapOpen,
    },
    ...(state === 'LIVE'
      ? [
          {
            key: 'roster',
            label: ca.sessionNow.roster,
            icon: 'people',
            onClick: () => scrollToCard('#roster'),
          },
        ]
      : []),
  ];

  const mods = def.modSet?.mods.length ?? 0;
  const dlcs = def.modSet?.dlcs?.length ?? 0;
  const worldLabel = data.cover?.worldDisplayName || def.terrain.worldName;
  const sides = <MissionSides sides={def.sides} modSetHash={def.terrain.modSetHash ?? null} />;

  // While it is live, what only makes sense live comes first: join.
  const joinAction = state === 'LIVE' && !stalled && data.server?.connectUrl && (
    <a className="button primary" href={data.server.connectUrl}>
      <i className="bi bi-play-fill" aria-hidden="true" />
      {ca.live.joinNow}
    </a>
  );
  const briefingLink = (primary = false) => (
    <Link className={primary ? 'button primary' : 'button'} to={`/s/${code}/briefing`}>
      <i className="bi bi-file-earmark-text" aria-hidden="true" /> {ca.briefing}
    </Link>
  );
  const qrButton = (
    <button type="button" onClick={() => setQr(!qr)} aria-pressed={qr}>
      <i className="bi bi-qr-code" aria-hidden="true" /> {ca.qr}
    </button>
  );

  // Whoever directs the night reads the same page a player does (the owner,
  // 2026-10-13: the author saw every objective marked, and a page that is
  // shared must show nobody a spoiler); what a director needs more is on the
  // direction page, and this is the clear way to it.
  const directionBand = data.canDirect && (
    <section className="band direction-band" aria-label={ca.sessionPage.directionTitle}>
      <i className="bi bi-compass" aria-hidden="true" />
      <div className="grow">
        <strong className="band-title">{ca.sessionPage.directionTitle}</strong>
        <p className="muted small">{ca.sessionPage.directionText}</p>
      </div>
      <Link className="button primary" to={`/direct/${code}`}>
        <i className="bi bi-sliders" aria-hidden="true" /> {ca.sessionPage.directionOpen}
      </Link>
    </section>
  );

  // The commander of the night takes the night's votes on this
  // session (study 6.3), and hands the command over from the band; nothing else
  // of the director's.
  const canDecide = data.canDirect || !!data.commandsTonight;
  const commanderBand =
    state !== 'ENDED' && data.commandsTonight ? (
      <CommanderBand code={code} onData={setData} onHandedOver={setHandedTo} onError={setError} />
    ) : (
      handedTo && (
        <p className="muted small" role="status">
          {ca.live.handedOver(handedTo)}
        </p>
      )
    );

  // What only whoever directs the night does from here: the session's code and
  // server, the start and the end.
  const directionRow = data.canDirect && state !== 'ENDED' && (
    <div className="row actions session-actions" role="group" aria-label={ca.sessionNow.actions}>
      {state === 'SCHEDULED' && (
        <button
          type="button"
          title={ca.rotateCodeHint}
          onClick={() =>
            void rotateSessionCode(code)
              .then((d) => navigate(`/s/${d.session.code}`))
              .catch((e) => setError(errorText(e)))
          }
        >
          {ca.rotateCode}
        </button>
      )}
      {(state === 'SCHEDULED' || stalled || (state === 'LIVE' && noServer)) && (
        <>
          {state === 'SCHEDULED' && (
            <label className="inline">
              {ca.sessionServer}
              <select
                aria-label={ca.sessionServer}
                title={ca.sessionServerHint}
                value={data.session.panelServerId ?? ''}
                onChange={(e) =>
                  void changeSessionServer(code, e.target.value ? Number(e.target.value) : null)
                    .then(setData)
                    .catch((err) => setError(errorText(err)))
                }
              >
                <option value="">{ca.chooseServerOption}</option>
                <ServerOptions rated={ratedServers} busyServers={busyServers} />
              </select>
            </label>
          )}
          {noServer && state !== 'SCHEDULED' && (
            <label className="inline">
              {ca.chooseServerToStart}
              <select
                aria-label={ca.chooseServerToStart}
                value={serverForStart ?? ''}
                onChange={(e) => setServerForStart(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">{ca.chooseServerOption}</option>
                {serversToPick.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            className="primary"
            disabled={
              !!starting || (noServer && (state === 'SCHEDULED' || serverForStart === null))
            }
            aria-busy={!!starting}
            title={starting ? ca.live.startingWait : undefined}
            onClick={() => {
              setStartBusy(true);
              void startSession(
                code,
                noServer && state !== 'SCHEDULED' ? (serverForStart ?? undefined) : undefined,
              )
                .then(setData)
                .catch((e) => setError(errorText(e)))
                .finally(() => setStartBusy(false));
            }}
          >
            {stalled || (state === 'LIVE' && noServer) ? ca.restartSession : ca.startSession}
          </button>
        </>
      )}
      {state === 'LIVE' && (
        <button
          type="button"
          onClick={() =>
            void (async () => {
              // A night ends once; one stray click on the header ended a live
              // session without a word until 2026-09-08.
              const yes = await confirm({
                title: ca.endSessionAsk,
                body: ca.endSessionBody,
                confirmLabel: ca.endSession,
                danger: true,
              });
              if (!yes) return;
              endSession(code, 'ended')
                .then(setData)
                .catch((e) => setError(errorText(e)));
            })()
          }
        >
          {ca.endSession}
        </button>
      )}
    </div>
  );

  const objectivesCard = (
    <ObjectivesCard
      objectives={shownObjectives}
      states={objectiveStates}
      titleOf={shownTitle}
      selected={selected}
      onSelect={(id) => setSelected((s) => (s === id ? null : id))}
      canEdit={editor.canEdit}
      onEdit={(id, patch) => void editor.save(id, patch)}
    />
  );

  // The map a player looks at: the area, the marks meant for them and the notes;
  // never the enemy, whom the editors placed for them to find.
  const playerMap = {
    world: def.terrain.worldName,
    worldSize: worldSizeOf(def),
    objectives: shownObjectives,
    start: def.sides.players.startPosition,
    area: def.area,
    heat: aftermath?.deathHeat,
    markers: (def.markers ?? []).filter(
      (m) => m.audience !== 'staff' && (m.revealAt ?? 'start') === 'start',
    ),
    intel: (def.intel ?? []).filter((n) =>
      ['briefing', 'start'].includes(n.revealAt ?? 'briefing'),
    ),
  };
  const mapCard = (
    <article className="card map-card">
      <div className="row">
        <h2 className="grow">{ca.sessionNow.barMap}</h2>
        <button type="button" className="small" onClick={() => setMapOpen(true)}>
          <i className="bi bi-arrows-fullscreen" aria-hidden="true" /> {ca.sessionNow.mapOpen}
        </button>
      </div>
      {!mapOpen && (
        <MissionMap
          {...playerMap}
          wide
          fit="area"
          selected={selected}
          onOpen={() => setMapOpen(true)}
          onSelect={(sel) =>
            setSelected((s) => (sel.kind === 'objective' && s === sel.id ? null : sel.id))
          }
        />
      )}
    </article>
  );
  // The supports of the night with their pictures, as the briefing draws them: before the night
  // and once it is over (web card 187, an ended session: «no hi surten els suports»). While it
  // runs, the SUPPORTS card says which is ready, each with its picture too.
  const supportsCard = (
    <article className="card" aria-label={ca.sessionPage.supportsTitle}>
      <h2>{ca.sessionPage.supportsTitle}</h2>
      <SupportTiles
        supports={def.supports}
        picks={data.supports}
        modSetHash={def.terrain.modSetHash}
        empty={ca.sessionPage.noSupports}
      />
    </article>
  );
  const campaignCard = data.campaign && (
    <section className="card" aria-label={ca.campaign}>
      <h2>
        <Link to={`/c/${data.campaign.code}`}>{data.campaign.title}</Link>{' '}
        <span className="chip">{ca.chapter(data.campaign.chapter)}</span>
      </h2>
      {data.campaign.delta ? (
        <>
          <p className="muted small">{ca.campaignDeltas}</p>
          <DeltaList delta={data.campaign.delta as ChapterDelta} />
        </>
      ) : null}
      {member &&
        state !== 'ENDED' &&
        votes.some((v) => v.key === 'nextChapter' && v.state === 'OPEN') && (
          <p className="muted small">{ca.nextChapterVoteOpen}</p>
        )}
    </section>
  );
  const timeline = (
    <SessionTimeline
      rows={timelineOf(events, votes, {
        shown: new Set(shownObjectives.map((o) => o.id)),
        names: member,
        ownSide: def.sides.players.side,
        classes: staff,
        ended: state === 'ENDED',
      })}
    />
  );
  // Whoever tests or administers finds their own at the foot of the page, as a
  // quiet row: a player's page reads the same with or without it.
  const quietLinks = member && <SessionTestsCard code={code} />;
  // Taking the night away, for the mission's own author and for an admin: the
  // sessions list had it for an admin alone and the page of the night had none.
  // A night in play is refused by the platform too; the button says why.
  const removeNight = async () => {
    const t = ca.sessionsPage2;
    const yes = await confirm({
      title: t.deleteTitle(data.missionTitle),
      body: t.deleteBody,
      confirmLabel: t.deleteConfirm,
      danger: true,
    });
    if (!yes) return;
    deleteSession(code)
      .then(() => navigate('/sessions'))
      .catch((e) => setError(errorText(e)));
  };
  const dangerZone = data.canDelete && (
    <div className="danger-zone">
      <button
        type="button"
        className="button small danger"
        disabled={state === 'LIVE'}
        title={state === 'LIVE' ? ca.sessionsPage2.deleteLiveHint : undefined}
        onClick={() => void removeNight()}
      >
        <i className="bi bi-trash" aria-hidden="true" /> {ca.sessionsPage2.delete}
      </button>
      {state === 'LIVE' && <span className="muted small">{ca.sessionsPage2.deleteLiveHint}</span>}
    </div>
  );
  const shell = {
    title: data.missionTitle,
    kicker: `${ca.session} · ${code}`,
    width: 'wide' as const,
    signInLabel: ca.sessionPage.signInAccount,
  };

  if (briefing) {
    // The orders, in the order they are read (the owner, 2026-10-12): what is
    // going on, where, what for, how, how we get there, what backs us; the
    // night's rules and the mods folded at the foot. Only what a player is
    // shown: an objective still hidden is not in the list, not even as "?".
    const visible = playerObjectives(def.objectives, objectiveStates);
    const hour = def.time?.hour;
    const night = typeof hour === 'number' && (hour < 6 || hour >= 20);
    const vehicles =
      data.vehicles ??
      (def.sides.players.vehicles ?? []).map((v) => ({
        className: v.className,
        displayName: v.label ?? '',
        count: v.count ?? 1,
        kind: v.kind ?? '',
      }));
    const supportKeys = supportKeysOf(def.supports);
    const cast = (def.cast ?? []).filter((c) => c.disposition !== 'hostile');
    const insertion = def.points?.insertion?.means;
    const extraction = def.points?.extraction?.means;
    const situation = def.briefing?.situation?.trim() || def.summary;
    // Where and when, and who is up against whom: the order's own opening, which the game
    // reads before the situation and the web left out (the owner, test card 159: too brief).
    const context = def.briefing?.context?.trim() || '';
    // The order's own sentence of the mission («L'equip ha de ... per tal de ...»),
    // written from the main line of the night: the objective, in general words.
    const missionLine = def.briefing?.mission?.trim() || '';
    // The order's execution, phase by phase, as the game reads it; the phases derived from the
    // objectives stand in for an order that has none.
    const executionText = def.briefing?.execution?.trim() || '';
    // What backs the night, as the order tells it, less its list of supports: the tiles above
    // are that list, with their pictures.
    const supportText = briefingSupportText(def.briefing?.support);
    const titleById = new Map(visible.map((o) => [o.id, shownTitle(o)]));
    return (
      <Shell {...shell} header={<BriefingStrip code={code} />}>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <SessionBanner
          cover={data.cover}
          world={def.terrain.worldName}
          label={ca.briefing}
          heading
          kicker={data.cover?.recipeTitle ?? ca.briefing}
          lead={data.missionTitle}
          facts={
            <>
              {typeof hour === 'number' && (
                <span className="chip num">
                  <i className={`bi bi-${night ? 'moon-stars' : 'sun'}`} aria-hidden="true" />{' '}
                  {ca.sessionPage.hourInGame(ca.sessionPage.hour(hour))}
                </span>
              )}
              {def.time?.weather && (
                <span className="chip">
                  <i className="bi bi-cloud-sun" aria-hidden="true" />{' '}
                  {ca.weatherNames[def.time.weather] ?? def.time.weather}
                </span>
              )}
              <span className="chip">
                <i className="bi bi-geo-alt" aria-hidden="true" /> {worldLabel}
              </span>
              <span className="chip num">
                <i className="bi bi-people" aria-hidden="true" />{' '}
                {ca.sessionPage.slots(def.sides.players.slots)}
              </span>
            </>
          }
        >
          {sides}
        </SessionBanner>
        <section className="cards briefing">
          <article className="card">
            <h2>{ca.summary}</h2>
            {context && <p className="situation context">{context}</p>}
            <p className="situation">{situation}</p>
          </article>
          {/* The place and what is asked there, side by side on a wide screen: the pins on the
              map carry the numbers the list beside it counts by (SE2b). */}
          <div className="briefing-where">
            <article className="card map-card" aria-label={ca.sessionPage.areaMap}>
              <div className="row">
                <h2 className="grow">{ca.sessionPage.areaMap}</h2>
                <button type="button" className="small" onClick={() => setMapOpen(true)}>
                  <i className="bi bi-arrows-fullscreen" aria-hidden="true" />{' '}
                  {ca.sessionNow.mapOpen}
                </button>
              </div>
              {/* A press on the map opens it over the whole page, as on the session page (card 160). */}
              {!mapOpen && (
                <MissionMap
                  {...playerMap}
                  wide
                  fit="area"
                  objectives={visible}
                  selected={selected}
                  onOpen={() => setMapOpen(true)}
                />
              )}
              <p className="muted small">
                {visible.length > 0 && `${ca.sessionPage.areaMapHint} `}
                {ca.sessionPage.areaMapGeneral}
              </p>
            </article>
            {/* What the night is for, apart from the situation (the owner, 2026-10-13: the two
              were mixed). The order's own sentence of the mission and, for each objective
              the players are shown, what it asks in general words by its type: never where
              the enemy is, never an objective still hidden. */}
            <article className="card" aria-label={ca.sessionPage.objectiveSection}>
              <h2>{ca.sessionPage.objectiveSection}</h2>
              {missionLine && <p className="objective-lead">{missionLine}</p>}
              {visible.length > 0 ? (
                <ol className="brief-objectives">
                  {visible.map((o, i) => (
                    <li key={o.id}>
                      <i
                        className={`bi bi-${OBJECTIVE_ICONS[o.type] ?? 'geo-alt'}`}
                        aria-hidden="true"
                      />
                      <div>
                        <strong>
                          {i + 1}. {shownTitle(o)}
                        </strong>{' '}
                        <span className="muted small">
                          {
                            ca.priorityNames[
                              isHiddenObjective(o) ? 'secondary' : (o.priority ?? 'main')
                            ]
                          }
                        </span>
                      </div>
                      {/* Three facts, each under its own word, on every objective (web card 187:
                          one said when it starts and the next did not): what it asks, when it
                          starts and what the map draws round it. */}
                      <dl className="general brief-facts">
                        <dt>{ca.sessionPage.objectiveAsks}</dt>
                        <dd>
                          {ca.sessionPage.objectiveByType[o.type] ??
                            ca.sessionPage.objectiveDefault}
                        </dd>
                        <dt>{ca.sessionPage.objectiveWhen}</dt>
                        <dd>{objectiveStart(o, titleById)}</dd>
                        <dt>{ca.sessionPage.objectiveOnMap}</dt>
                        <dd>
                          {typeof o.radius === 'number' && o.radius > 0
                            ? ca.sessionPage.objectiveCircle(
                                Math.round(o.radius / 50) * 50 || Math.round(o.radius),
                              )
                            : ca.sessionPage.objectiveNoCircle}
                        </dd>
                      </dl>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="muted small">{ca.sessionPage.objectiveNone}</p>
              )}
            </article>
          </div>
          <article className="card" aria-label={ca.execution}>
            <h2>{ca.execution}</h2>
            {executionText ? (
              <>
                <p className="situation">{executionText}</p>
                <p className="muted small">{ca.sessionPage.asInGame}</p>
              </>
            ) : (
              <>
                <ol className="plain numbered">
                  {derivePhases({ objectives: visible, points: def.points }).map((p, i) => (
                    <li key={i}>
                      <strong>
                        {p.kind === 'insertion'
                          ? ca.phaseInsertion(insertion ? ca.design.meansNames[insertion] : null)
                          : p.kind === 'extraction'
                            ? ca.phaseExtraction(
                                extraction ? ca.design.meansNames[extraction] : null,
                              )
                            : ca.phaseObjectives(p.objectives.map(shownTitle))}
                      </strong>
                    </li>
                  ))}
                </ol>
                <p className="muted small">{ca.phasesDerived}</p>
              </>
            )}
          </article>
          <article className="card" aria-label={ca.sessionPage.howWeGetThere}>
            <h2>{ca.sessionPage.howWeGetThere}</h2>
            {(insertion || extraction) && (
              <p className="row chips">
                {insertion && (
                  <span className="chip">
                    <i className="bi bi-box-arrow-in-right" aria-hidden="true" />{' '}
                    {ca.sessionPage.way(ca.design.meansNames[insertion])}
                  </span>
                )}
                {extraction && (
                  <span className="chip">
                    <i className="bi bi-box-arrow-right" aria-hidden="true" />{' '}
                    {ca.sessionPage.wayOut(ca.design.meansNames[extraction])}
                  </span>
                )}
              </p>
            )}
            {vehicles.length > 0 ? (
              <>
                <p className="muted small">{ca.sessionPage.vehiclesAtStart}</p>
                <ul className="plain start-vehicles">
                  {vehicles.map((v, i) => (
                    <li key={`${v.className}-${i}`}>
                      {/* The game's own picture of it; its class is never text. */}
                      <AssetPicture
                        thumb
                        size={140}
                        assetClass={v.className}
                        modSetHash={def.terrain.modSetHash}
                        name={v.displayName || ca.sessionPage.vehicle}
                      />
                      <strong>{v.displayName || ca.sessionPage.vehicle}</strong>
                      {v.count > 1 && (
                        <span className="chip num">{ca.sessionPage.vehicleCount(v.count)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="muted small">{ca.sessionPage.noVehicles}</p>
            )}
          </article>
          <article className="card" aria-label={ca.supportsWhich}>
            <h2>{ca.supportsWhich}</h2>
            <SupportTiles
              supports={def.supports}
              picks={data.supports}
              modSetHash={def.terrain.modSetHash}
              empty={ca.sessionPage.noSupports}
            />
            {supportKeys.length > 0 && <p className="muted small">{ca.supportsNoCountHint}</p>}
            {supportText && <p className="situation support-order">{supportText}</p>}
          </article>
          {cast.length > 0 && (
            <article className="card" aria-label={ca.cast}>
              <h2>{ca.cast}</h2>
              <ul className="plain">
                {cast.map((c) => (
                  <li key={c.id}>
                    <strong>{c.name}</strong> <span className="muted small">{c.role}</span>
                  </li>
                ))}
              </ul>
            </article>
          )}
          {kit.length > 0 && (
            <article className="card" aria-label={ca.kit}>
              <h2>{ca.kit}</h2>
              <ul className="plain kit">
                {kit.map((r, i) => (
                  <li key={`${r.role}-${i}`} className="kit-row">
                    <AssetPicture
                      assetClass={r.primary?.className}
                      kind="weapon"
                      modSetHash={def.terrain.modSetHash}
                      name={r.primary?.displayName}
                      size={48}
                    />
                    <div>
                      <strong>{r.label}</strong>
                      {r.count > 1 && <span className="muted small"> {ca.kitCount(r.count)}</span>}
                      {r.primary && <div>{r.primary.displayName}</div>}
                      <div className="muted small">
                        {r.loadout === null
                          ? ca.kitDefault
                          : r.items.map((it) => it.displayName).join(' · ')}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </article>
          )}
          {standings.length > 0 && (
            <article className="card" aria-label={ca.ranks}>
              <h2>{ca.ranks}</h2>
              <ul className="plain">
                {standings.map((s) => (
                  <li key={s.playerId}>
                    <strong>{s.name}</strong> <span className="chip">{s.rank}</span>{' '}
                    {s.medals.map((m) => (
                      <span key={m} className="chip">
                        {m}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </article>
          )}
          <div className="row briefing-read">
            {member && (
              <button
                type="button"
                className={data.briefingReadByMe ? '' : 'primary'}
                disabled={data.briefingReadByMe}
                onClick={() =>
                  void markBriefingRead(code)
                    .then((s) =>
                      setData({ ...data, briefingReads: s.reads, briefingReadByMe: s.readByMe }),
                    )
                    .catch((e) => setError(errorText(e)))
                }
                aria-pressed={data.briefingReadByMe}
              >
                <i className="bi bi-check2" aria-hidden="true" /> {ca.briefingRead}
              </button>
            )}
            <span className="muted small">{ca.briefingReads(data.briefingReads ?? 0)}</span>
          </div>
          {/* How the night works (MECHANICS.md R18) and the mods: read once, kept
              folded at the foot so the order itself comes first. */}
          <HowItWorksCard lines={def.briefing?.howItWorks} objectives={visible} collapsed />
          <LauncherMods code={code} mods={mods} dlcs={dlcs} collapsed />
          <Planned title={ca.briefing} ids={['briefing-print']} />
        </section>
        {mapOpen && (
          <SessionMapLayer
            map={{ ...playerMap, objectives: visible }}
            objectives={visible}
            states={objectiveStates}
            titleOf={shownTitle}
            selected={selected}
            onSelect={setSelected}
            onClose={() => setMapOpen(false)}
          />
        )}
      </Shell>
    );
  }

  const header = (
    <SessionHeader
      title={data.missionTitle}
      code={code}
      state={state}
      outcome={data.session.outcome}
      // Counted over what a player is shown: a hidden objective's own phase
      // would otherwise be one more in "Fase 2 de 5" before anybody knows of it.
      phase={
        state === 'LIVE'
          ? currentPhase({ objectives: shownObjectives, points: def.points }, objectiveStates)
          : null
      }
      elapsed={state === 'LIVE' && elapsed !== null ? ca.live.clock(elapsed) : null}
      inGame={inGame === null ? null : clockOfDay(inGame)}
      connection={connection}
      heartbeat={heartbeat}
    />
  );
  const untilStart = data.session.scheduledAt
    ? -(secondsFrom(data.session.scheduledAt, now) ?? 0)
    : null;
  const outcome = data.session.outcome ?? '';
  const duration =
    data.session.startedAt && data.session.endedAt
      ? Math.max(
          0,
          Math.floor(
            (new Date(data.session.endedAt).getTime() -
              new Date(data.session.startedAt).getTime()) /
              1000,
          ),
        )
      : null;
  // Who was there when the platform lists them (people only, never a headless
  // client), else the most the server ever reported, with its headless clients
  // taken off.
  const peak =
    attendees.length > 0
      ? attendees.length
      : (peakPlayers(events) ?? (snapshot ? snapshot.players.length || null : null));
  const fell = casualtyCounts(events, def.sides.players.side);
  const counted = events.some((e) => e.kind === 'casualty');
  const done = shownObjectives.filter((o) => objectiveStates.get(o.id)?.state === 'done').length;

  return (
    <Shell {...shell} header={header} bar={bar}>
      {confirmDialog}
      <ConnectionBanner
        connection={connection}
        online={live.online}
        onRetry={() => void live.refresh()}
      />
      {error && connection !== 'disconnected' && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {data.canDirect &&
        state === 'SCHEDULED' &&
        data.publishedRevision != null &&
        data.publishedRevision !== def.revision && (
          <p className="muted small" role="status">
            {ca.folderBehind(data.publishedRevision, def.revision)}
          </p>
        )}
      {directionBand}
      {starting && state !== 'ENDED' && <StartingBand step={starting.step} />}

      {state === 'SCHEDULED' && (
        <>
          {/* Before the night: an invitation. When, where, who against whom, how
              to get in, and whatever is left to a vote. */}
          <SessionBanner
            cover={data.cover}
            world={def.terrain.worldName}
            label={ca.live.scheduledTitle}
            kicker={ca.live.scheduledTitle}
            lead={
              untilStart === null
                ? ca.live.undated
                : untilStart <= 0
                  ? ca.live.startingNow
                  : ca.live.startsIn(ca.live.until(untilStart))
            }
            facts={
              <>
                {data.cover?.recipeTitle && <span className="chip">{data.cover.recipeTitle}</span>}
                <span className="chip">
                  <i className="bi bi-geo-alt" aria-hidden="true" /> {worldLabel}
                </span>
                <span className="chip num">
                  <i className="bi bi-people" aria-hidden="true" />{' '}
                  {ca.sessionPage.slots(def.sides.players.slots)}
                </span>
              </>
            }
          >
            {/* The date as a calendar tile and the hours left as figures that tick; a
                session with no hour says what starts it instead of drawing nothing. */}
            <SessionWhen scheduledAt={data.session.scheduledAt} now={now} showSeconds />
            {sides}
            <div className="row actions banner-actions">
              {briefingLink(true)}
              {qrButton}
            </div>
          </SessionBanner>
          {qr && <QrCode value={`${window.location.origin}/s/${code}`} label={ca.qrLabel} />}
          {directionRow}
          {commanderBand}
          <div className="dashboard">
            {/* Whatever is left to a vote, and the way to ask the direction for
                something: before the night only, and for members only. */}
            {member && (
              <VotesPanel
                code={code}
                votes={votes}
                canDirect={canDecide}
                onVotes={setVotes}
                onError={setError}
              />
            )}
            <div className="cards">
              <JoinCard
                server={data.server ?? null}
                when={fmtWhen(data.session.scheduledAt) ?? ca.live.undated}
              />
            </div>
            <section className="cards">
              <article className="card">
                <h2>{ca.summary}</h2>
                <p>{def.summary}</p>
              </article>
              {supportsCard}
            </section>
            <LauncherMods code={code} mods={mods} dlcs={dlcs} />
            {campaignCard}
          </div>
        </>
      )}

      {state === 'LIVE' && (
        <>
          <StateBand
            state={state}
            stalled={starting ? null : stallReason}
            noServer={noServer && !starting}
          />
          <AwayBand away={away} now={now} />
          {/* While the night runs: that it is on, for how long and with how many,
              and the way in before anything else. */}
          <section className="band live-status" aria-label={ca.sessionPage.statusStrip}>
            {!stalled && !noServer && (
              <span className="chip live">
                <span className="dot live" aria-hidden="true" />
                {ca.sessionPage.inPlay}
              </span>
            )}
            {elapsed !== null && (
              <span className="num">{ca.sessionPage.minute(Math.floor(elapsed / 60))}</span>
            )}
            {onServer !== null && (
              <span className="num">
                <i className="bi bi-people" aria-hidden="true" /> {ca.sessionPage.players(onServer)}
              </span>
            )}
            <span className="grow" />
            {joinAction}
            {briefingLink()}
            {qrButton}
          </section>
          {qr && <QrCode value={`${window.location.origin}/s/${code}`} label={ca.qrLabel} />}
          {directionRow}
          {commanderBand}
          {/* What is happening now, what I have to decide, what can be asked for
              (UI doc 5.1, principle 1): the map and the timeline come after. */}
          <div className="now-stack">
            <section className="cards">
              <NowCard
                objective={currentObjective}
                title={currentObjective ? shownTitle(currentObjective) : ''}
                state={
                  currentObjective
                    ? (objectiveStates.get(currentObjective.id)?.state ?? 'pending')
                    : ''
                }
                grid={currentObjective ? gridRef(currentObjective.position) : null}
                distance={currentObjective ? distanceTo(events, currentObjective.id) : null}
                holdClock={currentObjective ? holdClockOf(events, currentObjective.id) : null}
                now={now}
                progress={progressOf(def.objectives, objectiveStates)}
                radio={latestRadio(events)}
                runtimeUp={runtimeUp}
                onOpen={openOnMap}
              />
              <HowItWorksCard
                lines={def.briefing?.howItWorks}
                objectives={shownObjectives}
                collapsed
              />
            </section>
            {/* No vote while the session is in play (the owner, 2026-10-12): votes are
                held before it starts. */}
            <section className="cards">
              <SupportsCard
                rows={supports.rows}
                picks={data.supports}
                modSetHash={def.terrain.modSetHash}
                fromSnapshot={supports.fromSnapshot}
                snapshotAt={snapshot?.receivedAt ?? null}
                decision={latestCommandDecision(events)}
                now={now}
              />
            </section>
          </div>
          <div className="split">
            <section className="cards">
              {mapCard}
              {objectivesCard}
            </section>
            <aside className="side">
              <JoinCard server={data.server ?? null} />
              <RosterCard
                commander={data.commander}
                snapshot={snapshot}
                live={data.liveRoster ?? []}
                players={onServer}
                briefingReads={data.briefingReads ?? 0}
                now={now}
                names={member}
              />
              {campaignCard}
              {timeline}
              <LauncherMods code={code} mods={mods} dlcs={dlcs} />
            </aside>
          </div>
          <Planned ids={['session-map-positions', 'session-map-marker']} />
        </>
      )}

      {state === 'ENDED' && (
        <>
          {/* Once it is over: how it ended, in large, and what it left. Nothing
              to vote, nothing to ask for, nowhere to join. */}
          <SessionBanner
            cover={data.cover}
            world={def.terrain.worldName}
            label={ca.live.outcomeBanner}
            kicker={ca.sessionPage.result}
            lead={ca.outcomes[outcome] ?? (outcome || ca.sessionPage.resultUnknown)}
            tone={
              outcome === 'success'
                ? 'ok'
                : outcome === 'failed' || outcome === 'failure'
                  ? 'bad'
                  : undefined
            }
            facts={
              <>
                {duration !== null && (
                  <span className="chip num">
                    <i className="bi bi-stopwatch" aria-hidden="true" />{' '}
                    {ca.sessionPage.duration(ca.live.until(duration))}
                  </span>
                )}
                {(data.session.endedAt || data.session.startedAt) && (
                  <span className="chip">
                    <i className="bi bi-calendar-event" aria-hidden="true" />{' '}
                    {fmtWhen(data.session.startedAt ?? data.session.endedAt)}
                  </span>
                )}
                {peak !== null && (
                  <span className="chip num">
                    <i className="bi bi-people" aria-hidden="true" /> {ca.sessionPage.players(peak)}
                  </span>
                )}
                <span className="chip">
                  <i className="bi bi-geo-alt" aria-hidden="true" /> {worldLabel}
                </span>
              </>
            }
          >
            {outcome === 'partial' && <p>{ca.live.partialLine}</p>}
            <div className="row actions banner-actions">
              {data.reportUrl && (
                <a
                  className="button primary"
                  href={data.reportUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="bi bi-file-earmark-bar-graph" aria-hidden="true" /> {ca.report}
                </a>
              )}
              {briefingLink()}
            </div>
          </SessionBanner>
          <section className="card figures-card" aria-label={ca.sessionPage.figures}>
            <h2>{ca.sessionPage.figures}</h2>
            <dl className="figures">
              <Figure
                value={`${done}/${shownObjectives.length}`}
                label={ca.sessionPage.objectivesLabel}
              />
              {counted && <Figure value={fell.own} label={ca.sessionPage.ownCasualties} />}
              {counted && <Figure value={fell.enemy} label={ca.sessionPage.enemyCasualties} />}
              {counted && fell.civilian > 0 && (
                <Figure value={fell.civilian} label={ca.sessionPage.civilianCasualties} />
              )}
              {peak !== null && <Figure value={peak} label={ca.sessionPage.playersLabel} />}
              {duration !== null && (
                <Figure value={ca.live.until(duration)} label={ca.sessionPage.durationLabel} />
              )}
            </dl>
          </section>
          <div className="split outcome-split">
            <section className="cards">
              {mapCard}
              {objectivesCard}
              {/* The night's story is the longest thing on the page: in the wide column,
                  under the objectives, it fills the height the narrow one used to leave
                  empty beside it (the owner, 2026-10-26, card 262). */}
              {timeline}
            </section>
            <aside className="side">
              <OutcomeMiniMap
                map={{
                  world: playerMap.world,
                  worldSize: playerMap.worldSize,
                  area: playerMap.area,
                }}
                objectives={shownObjectives}
                states={objectiveStates}
              />
              {member && (
                <WhoWasThere
                  people={
                    attendees.length > 0
                      ? attendees
                      : (snapshot?.players ?? []).map((p) => ({ name: p.name }))
                  }
                  hint={attendees.length > 0 ? ca.sessionPage.attendeesHint : undefined}
                />
              )}
              {supportsCard}
              {campaignCard}
              {member && data.campaign && (
                <NextChapterCard
                  campaignCode={data.campaign.code}
                  code={code}
                  votes={votes}
                  onVotes={setVotes}
                  onError={setError}
                />
              )}
              {/* The report's «Fases»: the phases a scripted night went through, in
                  order, with their minute; the platform hands a player nothing
                  before the night is over (engine step E12, R16). */}
              <ScriptPhases code={code} />
              {aftermath && (
                <section className="card" aria-label={ca.reportsPage2.detail}>
                  <h2>{ca.reportsPage2.detail}</h2>
                  {/* The panel counted all of this; what this page adds is the
                      map above, where the deaths sit on the objectives they
                      were meant to be near. */}
                  <MatchTables detail={aftermath} />
                </section>
              )}
            </aside>
          </div>
        </>
      )}

      {quietLinks}
      {dangerZone}
      {mapOpen && (
        <SessionMapLayer
          map={playerMap}
          objectives={shownObjectives}
          states={objectiveStates}
          titleOf={shownTitle}
          selected={selected}
          onSelect={setSelected}
          onClose={() => setMapOpen(false)}
        />
      )}
    </Shell>
  );
}
